//@ts-nocheck
import { FC, Fragment, useEffect, useState } from 'react';

import { Accordion, Box, Divider, Flex, LoadingOverlay, Text, Title } from '@mantine/core';

import { useQuery } from '@tanstack/react-query';

import useLogout from '@/hooks/useLogout.ts';

import { useCreatePersonSettings, useUpdatePersonSettings } from '@/entities/Settings';

import { bookingObjectsApi } from '@/shared/api/bookingObjects';
import { getLocationType, removeAttributes, stringifyObjectValues } from '@/shared/helpers/utils';
import useAppStore from '@/shared/store/app.store';
import useBookingStore from '@/shared/store/booking.store.ts';
import { getSettings, useSettingsStore } from '@/shared/store/settings.store.ts';
import useUserStore from '@/shared/store/user.store.ts';
import { ILocation } from '@/shared/types/app.types';
import { BookingType, IObject } from '@/shared/types/bookingObjects.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { Checkbox } from '@/shared/uikit/components/checkbox/Checkbox';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';

import { IconMapPinOff } from '@tabler/icons-react';

type Props = {
	title: string;
	bookingType?: BookingType;
	withBackButton?: boolean;
	open?: boolean;
	isClosable?: boolean;
	close: () => void;
	onChange?: (location: string) => void;
};

const getParking = (objects: IObject[]): IObject[] => {
	let children: IObject[] = [];

	return objects
		.filter((object) => !object.type.is_bookable)
		.map((m) => {
			if (m.children && m.children.length) {
				children = [...children, ...m.children];
			}

			return m;
		})
		.concat(children.length ? getParking(children) : children);
};

export const LocationSelectorSheet = ({
	title,
	withBackButton = false,
	open = true,
	close,
	isClosable = true,
	onChange,
}: Props) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const user = useUserStore((state) => state.user);
	const settingsId = useSettingsStore((state) => state.id);
	const location = useSettingsStore((state) => state.location);
	const currentLocation = useSettingsStore((state) => state.currentLocation);
	const setCurrentLocation = useSettingsStore((state) => state.setCurrentLocation);
	const setLocation = useSettingsStore((state) => state.setLocation);
	// const { location, currentLocation, setCurrentLocation, setLocation } = useAppStore();
	const { bookingPlace, setBookingPlace } = useBookingStore();
	const logout = useLogout();
	const [selectedLocation, setSelectedLocation] = useState<IObject | null>(currentLocation ?? null);
	const [initLocation, setInitLocation] = useState<ILocation | null>(location);
	const [test, setTest] = useState<[IObject[]] | null>(null);

	const { createSettings, isCreatingSettings } = useCreatePersonSettings();
	const { updateSettings, isUpdatingSettings } = useUpdatePersonSettings();

	const { data: rootObjects, isLoading: isLoadingRootObjects } = useQuery({
		queryKey: ['rootObjects'],
		queryFn: bookingObjectsApi.getRootObjects,
	});
	const handleSelectRoot = (selectedObject: IObject) => {
		const { parent_id } = selectedObject;

		const children = selectedObject.children.filter(
			(child) => !child.type.is_bookable && getLocationType(child.type.name) !== 'parking',
		);
		const parking = getParking(selectedObject.children).filter(
			(obj) => getLocationType(obj.type.name) === 'parking',
		);

		if (bookingPlace) {
			setBookingPlace(null);
		}

		const isObject = test?.find((item) => item.some((el) => el.parent_id === parent_id));

		if (isObject) {
			const idx = test?.indexOf(isObject);
			if (idx === null || idx === undefined) return null;

			setTest([...(test?.slice(0, idx + 1) as []), [...children]]);

			if (initLocation && initLocation.route) {
				const existsObject = Object.values(initLocation.route).find(
					(obj) => obj.parent_id === parent_id,
				);

				if (existsObject) {
					const currentIdx = initLocation.route.indexOf(existsObject);
					const newLocation = [...initLocation.route.slice(0, currentIdx + 1)];
					newLocation[currentIdx] = selectedObject;
					setInitLocation({
						...initLocation,
						...(parking.length ? { parking: parking } : { parking: [...initLocation.parking] }),
						route: [...newLocation],
					});
				} else {
					setInitLocation({
						...initLocation,
						...(parking.length ? { parking: parking } : { parking: [...initLocation.parking] }),
						route: [...initLocation.route, selectedObject],
					});
				}
			} else {
				setInitLocation({
					parking: parking,
					route: [selectedObject], //TODO: create custom type
				});
			}
		}

		// setCurrentLocation(selectedObject);
		setSelectedLocation(selectedObject);
	};

	const handleSave = () => {
		if (onChange) {
			const locationPath = initLocation?.route?.map((r) => r?.name);

			onChange(selectedLocation, locationPath);
			close();
		} else {
			const settings = getSettings();

			if (initLocation && Object.keys(initLocation).length) {
				const newSettings: { [key: string]: unknown } = {
					...settings,
					location: initLocation,
					currentLocation: selectedLocation,
				};

				stringifyObjectValues(newSettings);

				settingsId
					? updateSettings({ settingsId: String(settingsId), payload: { ...newSettings } })
					: createSettings({ personId: user!.id, payload: { ...newSettings } });

				setLocation(initLocation);
				setCurrentLocation(selectedLocation);
				close();
			}
		}
	};

	const handleLogout = () => {
		void logout();
	};

	useEffect(() => {
		if (rootObjects) {
			const allowedProps = [
				'id',
				'parent_id',
				'type',
				'name',
				'description',
				'booking_pass_type_id',
				'moderator_pass_type_id',
				'children',
			];

			setTest([removeAttributes(rootObjects, allowedProps)]);
		}
	}, [rootObjects]);

	const locationSelector = (
		<Box pos="relative" h="100%">
			<LoadingOverlay
				visible={isLoadingRootObjects}
				zIndex={1000}
				overlayProps={{ radius: 2, blur: 2 }}
			/>
			{rootObjects && !rootObjects.length && (
				<Flex direction="column" align="center" justify="center" h="100%">
					<IconMapPinOff size={64} color="#f1f1f1" />
					<Text fw={600} ta="center">
						Нет доступных локаций. Пожалуйста, обратитесь к администратору системы.
					</Text>
				</Flex>
			)}

			{rootObjects &&
				test?.map((item) => {
					if (!item.length) return;

					return (
						<Accordion key={Math.random()} defaultValue={String(rootObjects[0]?.parent_id)}>
							<Accordion.Item
								key={rootObjects[0]?.parent_id}
								value={String(rootObjects[0]?.parent_id)}
							>
								<Accordion.Control>
									<Title size="h3" fw={800} fz={20}>
										{item[0]?.type.name}
									</Title>
								</Accordion.Control>
								<Accordion.Panel>
									{item.map((root) => {
										return (
											<Fragment key={root.id}>
												<Flex
													justify="space-between"
													gap={16}
													onClick={() => handleSelectRoot(root)}
													style={{ cursor: 'pointer' }}
												>
													<Flex direction="column">
														<Text size="md" fz={16} fw={600} lts="normal">
															{root.name}
														</Text>
													</Flex>
													<Flex align="center" gap={18}>
														<Checkbox checked={selectedLocation?.id === root?.id} />
													</Flex>
												</Flex>
												{item[item.length - 1] !== root && <Divider my={12} color="#6670851A" />}
											</Fragment>
										);
									})}
								</Accordion.Panel>
							</Accordion.Item>
						</Accordion>
					);
				})}
		</Box>
	);

	if (isMobile)
		return (
			<BottomSheet
				onClose={close}
				opened={open}
				title={title}
				buttons={
					!rootObjects || !!rootObjects.length
						? [
								{
									children: 'Сохранить',
									...(!selectedLocation ? { variant: 'light' } : {}),
									onClick: handleSave,
								},
							]
						: [
								{
									children: 'Выйти',
									onClick: handleLogout,
								},
							]
				}
				withCloseButton={false}
				withBackButton={withBackButton}
				isSwipeable={rootObjects && !!rootObjects.length}
				isFullScreen
			>
				{locationSelector}
			</BottomSheet>
		);

	return (
		<ModalSheet
			closeOnEscape={false}
			opened={open}
			onClose={handleSave}
			title={title}
			withCloseButton={false}
			size="lg"
			buttons={
				!rootObjects || !!rootObjects.length
					? [
							{
								children: 'Сохранить',
								...(!selectedLocation ? { variant: 'light' } : {}),
								onClick: handleSave,
							},
						]
					: [
							{
								children: 'Выйти',
								onClick: handleLogout,
							},
						]
			}
			centered
		>
			<Box style={{ overflowY: 'auto' }} h="60dvh" pr="md">
				{locationSelector}
			</Box>
		</ModalSheet>
	);
};
